/** Passkey (WebAuthn) enrolment helpers.
 *
 * The register flow is: begin (server issues creation options) → the browser
 * prompts the authenticator (startRegistration) → complete (server verifies +
 * stores the credential). @simplewebauthn/browser handles all the
 * base64url↔ArrayBuffer conversion and consumes py_webauthn's JSON directly.
 */
import {
  browserSupportsWebAuthn,
  startRegistration,
} from "@simplewebauthn/browser";

import { ApiPaths, api } from "./api";

export type Passkey = {
  id: string;
  name: string;
  created_at: string;
  last_used_at: string | null;
};

/** Best-effort friendly name for a new passkey, derived from the device — so
 *  enrolment doesn't have to prompt the user. */
export function defaultPasskeyName(): string {
  const nav = navigator as Navigator & {
    userAgentData?: { platform?: string };
  };
  const hint = `${nav.userAgentData?.platform ?? ""} ${navigator.userAgent ?? ""}`;
  const is = (re: RegExp) => re.test(hint);
  if (is(/iphone/i)) return "iPhone";
  if (is(/ipad/i)) return "iPad";
  if (is(/mac/i)) return "Mac";
  if (is(/windows/i)) return "Windows";
  if (is(/android/i)) return "Android";
  if (is(/linux/i)) return "Linux";
  return "Passkey";
}

/** Enrol a new passkey for the signed-in user. Auto-names it from the device
 *  when no explicit name is given. Throws on cancel/failure. */
export async function registerPasskey(name?: string): Promise<Passkey> {
  const finalName = (name && name.trim()) || defaultPasskeyName();
  const begin = await api.post(ApiPaths.webauthnRegisterBegin, {});
  const credential = await startRegistration({ optionsJSON: begin.data });
  const res = await api.post<Passkey>(ApiPaths.webauthnRegisterComplete, {
    credential,
    name: finalName,
  });
  return res.data;
}

export async function listPasskeys(): Promise<Passkey[]> {
  const res = await api.get<Passkey[]>(ApiPaths.webauthnCredentials);
  return res.data;
}

export async function deletePasskey(id: string): Promise<void> {
  await api.delete(ApiPaths.webauthnCredential(id));
}

export { browserSupportsWebAuthn };
